import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiStar, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import dataService from '../services/dataService';

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const iframeRefs = useRef({});

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const data = await dataService.getVideos();
        setVideos(data);
      } catch (error) {
        console.error('Failed to load videos:', error);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <SEO
        pageType="videos"
        title="Patient Success Videos | Real Healing Transformations | JIVA NADI"
        description="Watch real patient success stories and healing transformations. Authentic testimonials from individuals who found relief through natural treatments."
        keywords="patient videos, success stories, healing transformations, testimonial videos, chiropractic results, pain relief success"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Real Patient Transformations"
        subtitle="Success Stories"
        description="Watch authentic healing journeys of patients who found relief through natural treatments when traditional medicine fell short."
        image="https://images.unsplash.com/photo-1540655037529-dec987208707?w=800&h=600&fit=crop"
        badge="Patient Success Videos"
        primaryButton={{
          text: "Watch Stories",
          href: "#videos"
        }}
        secondaryButton={{
          text: "Book Consultation",
          href: "/contact"
        }}
        trustIndicators={[
          { value: "1000+", label: "Success Stories" },
          { value: "4.9/5", label: "Patient Rating" },
          { value: "95%", label: "Recovery Rate" }
        ]}
      />

      {/* Videos Grid */}
      <section id="videos" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
              <SafeIcon icon={FiPlay} className="w-4 h-4" />
              <span>Watch Real Patient Stories</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              Hear From Those We've Helped
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Real patients, real results. Watch authentic transformation stories from individuals who chose natural healing.
            </p>
          </motion.div>

          {/* Videos Grid */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {videos.map((video, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-2xl hover:border-emerald-200 transition-all duration-500">
                  {/* Video Container */}
                  <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
                    <iframe
                      ref={el => iframeRefs.current[video.youtubeId] = el}
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${video.youtubeId}?enablejsapi=1`}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>

                  {/* Video Info */}
                  <div className="p-6 lg:p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full mb-3">
                          {video.category} Success Story
                        </span>
                        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 leading-tight group-hover:text-emerald-600 transition-colors">
                          {video.title}
                        </h3>
                        <p className="text-gray-600 font-medium flex items-center gap-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500" />
                          Patient: {video.patient}
                        </p>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-1 pt-4 border-t border-gray-100">
                      <div className="flex text-amber-400">
                        {[...Array(5)].map((_, i) => (
                          <SafeIcon key={i} icon={FiStar} className="w-5 h-5 fill-current" />
                        ))}
                      </div>
                      <span className="ml-2 text-sm font-semibold text-gray-700">5.0</span>
                      <span className="text-sm text-gray-500 ml-1">Verified Patient</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 rounded-3xl p-8 lg:p-12 text-center"
          >
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Write Your Success Story?
            </h3>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join thousands of patients who found relief through natural healing. Book your consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-emerald-700 px-8 py-4 rounded-full font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Free Consultation
              </a>
              <a
                href="/testimonials"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-emerald-700 transition-all duration-300"
              >
                Read More Stories
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VideosPage;